import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getLifebooksChildrenSort } from 'redux/selectors';
import moment from 'moment';
import { usePasswordConfirm } from 'helpers';
import { useCreateDispatches } from 'helpers/hooks';
import {
	PageLayout,
	SectionHeader,
	PrimaryButton,
	SecondaryButton,
} from 'components';
import * as services from 'services';
import {
	transferStatusLabel,
	isOpenTransfer,
} from '../utils/transferStatusLabel';
import styles from './ChildAdmin/ChildAdmin.module.css';

const errorMessage = (e) =>
	e?.response?.data?.message || 'This request cannot be processed at this time.';

export default function Transfers() {
	const [transfers, setTransfers] = useState({ incoming: [], outgoing: [] });
	const { setSnackbar, setLoader } = useCreateDispatches();
	const onPasswordConfirm = usePasswordConfirm();
	const childSort = useSelector(getLifebooksChildrenSort);

	const load = () =>
		services
			.getTransfers()
			.then(setTransfers)
			.catch((e) => setSnackbar({ variant: 'error', message: errorMessage(e) }));

	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const act = async (action, id) => {
		setLoader(true);
		try {
			await services[action](id);
			await load();
		} catch (e) {
			setSnackbar({ variant: 'error', message: errorMessage(e) });
		} finally {
			setLoader(false);
		}
	};

	// PasswordConfirm shows a thrown error inline and keeps the dialog open.
	const release = (t) =>
		onPasswordConfirm(
			async (pass) => {
				await services.releaseTransfer(t.id, pass);
				await load();
				// The released child must leave this agency's children list right away.
				await services.setChildrenList(childSort);
				setSnackbar({
					variant: 'success',
					message: `${t.childName} released to ${t.toAgencyName}`,
				});
			},
			{
				message: `Releasing moves ${t.childName}'s LifeBook to ${t.toAgencyName} and cannot be undone. Enter your password to release.`,
				submitLabel: 'Release',
			}
		);

	const row = (t, incoming) => (
		<div className={styles.childAdminItemWrapper} key={t.id}>
			<div>
				<div className={styles.childAdminBoldLabel}>
					{t.childName}
					{t.childAge != null ? `, age ${t.childAge}` : ''}
				</div>
				<div>
					{incoming
						? `From ${t.fromAgencyName}`
						: `To ${t.toAgencyName} (${t.recipientName})`}
				</div>
				<div>
					{transferStatusLabel(t.status, incoming)}
					{isOpenTransfer(t.status)
						? ` · Expires ${moment(t.expiresAt).format('MMM D, YYYY')}`
						: ''}
				</div>
			</div>
			<div className={styles.childAdminButtonContainer}>
				{incoming && t.status === 'pending' && (
					<>
						<SecondaryButton onClick={() => act('declineTransfer', t.id)}>
							Decline
						</SecondaryButton>
						<PrimaryButton onClick={() => act('acceptTransfer', t.id)}>
							Accept
						</PrimaryButton>
					</>
				)}
				{!incoming && isOpenTransfer(t.status) && (
					<SecondaryButton onClick={() => act('cancelTransfer', t.id)}>
						Cancel
					</SecondaryButton>
				)}
				{!incoming && t.status === 'accepted' && (
					<PrimaryButton onClick={() => release(t)}>Release</PrimaryButton>
				)}
			</div>
		</div>
	);

	const section = (title, list, incoming) => (
		<>
			<SectionHeader>{title}</SectionHeader>
			{list.length ? list.map((t) => row(t, incoming)) : <p>No transfers.</p>}
		</>
	);

	return (
		<PageLayout title='Transfers' align='left'>
			{section('Incoming', transfers.incoming || [], true)}
			{section('Outgoing', transfers.outgoing || [], false)}
		</PageLayout>
	);
}
